<template>
    <div class="d-flex flex-column case-session-container">
        <div class="flex-grow-0">
            <nav
                class="
                    d-flex
                    align-items-md-start
                    flex-wrap flex-md-nowrap
                    justify-content-end justify-content-lg-start
                    mt-2
                    mx-2
                "
            >
                <div class="flex-grow-1 session-navbar">
                    <ul class="d-flex">
                        <li class="nav-item flex-grow-0 flex-shrink-0">
                            <router-link class="nav-link text-secondary nowrap px-1 px-sm-3" :to="returnURL">
                                <span class="icon icon-sm icon-glyphicons-basic-chevron-left mr-1 align-middle"></span>
                                <span class="align-middle d-none d-sm-inline">{{ returnText }}</span>
                            </router-link>
                        </li>
                    </ul>
                </div>
                <div class="flex-grow-0 session-navbar">
                    <li class="nav-item d-none d-md-block mr-3">
                        <span class="nav-link d-flex">
                            <div v-show="editMode && fullScreenIsActive && !loading">
                                <button class="btn btn-gray badge badge-gray mr-3" @click="openEditCurrentFullscreen()">
                                    <span class="icon icon-glyphicons-basic-square-edit align-middle mr-xs"></span>
                                    <span class="align-middle" style="margin-right: 2px;">Edit</span>
                                </button>
                            </div>
                            <div v-show="editMode && videoIsPlaying && !loading">
                                <button class="btn btn-gray badge badge-gray mr-3" @click="skipVideos()">
                                    <span class="align-middle" style="margin-right: 2px;">Skip Video</span>
                                    <span class="icon icon-glyphicons-basic-skip-to-next align-middle"></span>
                                </button>
                            </div>
                            <div v-if="editMode && currentNode" class="dropdown" ref="nodeDropdown">
                                <session-node-dropdown
                                    :current-node="currentNode"
                                    v-on:select="viewNode($event)"
                                ></session-node-dropdown>
                            </div>
                            <span v-show="!editMode" class="badge badge-gray">{{ currentNodeLabel }}</span>
                        </span>
                    </li>
                </div>
                <div class="flex-grow-0 align-self-end nav-scroll-x">
                    <session-toolbar
                        v-if="sessionInitialized && sessionCase"
                        :case-obj="sessionCase"
                        :show-comments-btn="false"
                        :show-author-tools="true"
                        v-on:open-create-panel="openCreatePanel()"
                        v-on:timer-expired="timerExpired($event)"
                        v-on:reset-session="resetPreviewSession()"
                    ></session-toolbar>
                </div>
            </nav>
        </div>
        <case-session
            :session-id="sessionID"
            v-on:nodeChange="setCurrentNode($event)"
            v-on:session-not-found="handleSessionNotFound()"
            :sessionInitialized.sync="sessionInitialized"
            ref="caseSession"
        ></case-session>
    </div>
</template>

<script>
import http from "@/axios.js"
import CaseSession from "@/components/CaseSession"
import SessionToolbar from "@/components/SessionToolbar"
import SessionNodeDropdown from "@/components/SessionNodeDropdown"

export default {
    name: "PreviewCaseSession",
    components: {
        CaseSession,
        SessionToolbar,
        SessionNodeDropdown,
    },
    data() {
        return {
            currentNode: null,
            sessionInitialized: false,
            currentNodeLabel: "",
        }
    },
    computed: {
        loading() {
            return this.$store.getters.loading
        },
        caseID() {
            return this.$route.params.caseID
        },
        editMode() {
            return this.$store.getters.previewEditMode
        },
        caseIsReadOnly() {
            return this.$store.getters.caseIsReadOnly
        },
        sessionID() {
            return this.$route.params.sessionID
        },
        sessionCase() {
            return this.$store.getters.sessionCase
        },
        currentSession() {
            return this.$store.getters.session
        },
        caseObj() {
            return this.$store.getters.case
        },
        currentNodeId() {
            if (!this.currentNode) {
                return null
            }
            return this.currentNode.id
        },
        baseEditorUrl() {
            return `/library/${this.caseID}`
        },
        returnURL() {
            let suffix = ""
            if (this.$route.query.fromView == "map") {
                suffix = `/map?nodeID=${this.currentNodeId}&scrollToNavItem=true`
            } else {
                suffix = "/nodes/" + this.currentNodeId
            }
            return this.baseEditorUrl + suffix
        },
        returnText() {
            if (this.$route.query.fromView == "map") {
                return "Back to Map"
            } else {
                return "Back to Settings"
            }
        },
        communityViewActive() {
            return this.$route.path.startsWith("/community")
        },
        nodeOptions() {
            return this.$store.getters.nodeOptions
        },
        videoIsPlaying() {
            return this.$store.getters.videoIsPlaying
        },
        fullScreenIsActive() {
            return this.$store.getters.fullScreenIsActive
        },
    },
    methods: {
        // Handles the node changing in the child case session element.
        setCurrentNode(node) {
            this.currentNode = node
            if (node.title) {
                this.currentNodeLabel = `${node.number}. ${node.title}`
            }
            this.$store.commit("setSessionNode", node)
            // Store the current node number in the query parameters of the URL. This is useful
            // for bringing the user back to the right screen if they store / send URLs copied
            // during preview sessions.
            this.$router.replace({ query: { ...this.$route.query, nodeId: node.id } }).catch(error => {
                if (error.name !== 'NavigationDuplicated') {
                    console.error(error)
                }
            })
        },
        timerExpired(timerId) {
            this.$refs.caseSession.timerExpired(timerId)
            this.$refs.caseSession.updateSessionScore()
        },
        refreshSession() {
            this.$refs.caseSession.refreshSession()
        },
        // Open the create panel modal.
        openCreatePanel() {
            // TODO: Add a function in the CaseSession component to open the create dialog with
            // positioning set to some default next available position.
        },
        handleSessionNotFound() {
            // User may have tried to access an old preview session, so just route them
            // to the editor view and clear the errors thrown. In the future, may want to
            // route them to a dedicated 'session removed' page for better UX/clarity.
            this.$router.push(this.baseEditorUrl + "/settings")
            this.$notify({
                group: "main",
                clean: true,
            })
        },
        // View a different node using the same session.
        viewNode(node) {
            if (this.currentNodeId === node.id) {
                return
            }
            this.$store.dispatch("addLoad")
            let vm = this
            http.put(`/api/sessions/${this.sessionID}/switch-nodes/`, {
                nodeId: node.id,
            })
                .then(() => {
                    vm.$refs.caseSession.refreshSession()
                })
                .finally(() => {
                    vm.$store.dispatch("removeLoad")
                })
        },
        skipVideos() {
            this.$refs.caseSession.skipVideos()
        },
        openEditCurrentFullscreen() {
            this.$refs.caseSession.openEditCurrentFullscreen()
        },
        resetPreviewSession() {
            this.$refs.caseSession.resetPreviewSession()
        },
    },
    mounted() {
        if (!this.caseObj.id) {
            this.$store.dispatch("fetchCase", this.caseID)
        }
    },
    // Called by the router before navigating away from this component.
    beforeRouteLeave(to, from, next) {
        this.$refs.caseSession.closeEditModals()
        next()
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";

li {
    list-style-type: none;
}

ul {
    padding-left: 0px;
    padding-right: 0px;
    margin-bottom: 0px;
}

.nav-item {
    margin-top: 3px;
}
</style>